import {
  Box,
  Card,
  DELETE_WARNING,
  DataDisplay,
  DeleteIconButton,
  Grid,
  GridBreak,
  IconButton,
  List,
  RichTextEditor,
  RichTextReader,
  Stack,
  Switch,
  Tooltip,
  Typography,
  ValidationErrors,
  enumContains,
  pn,
} from "@compass/core";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Input, Select } from "components";
import { MyDroppable } from "components/common/Draggable/Droppable";
import {
  Form,
  FormAnswer,
  FormQuestion,
  FormSection,
  FormSubQuestion,
  LkFormQuestionType,
  SurveyViewResource,
} from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { createEntity } from "utils/CreateEntity";
import { reorder } from "utils/Reorder";
import { SurveyQuestionAnswer } from "./SurveyQuestionAnswer";
import {
  BranchableFormQuestionTypes,
  FormQuestionTypes,
  FormQuestionTypesWithAnswers,
  FormQuestionTypesWithSubQuestions,
} from "./SurveyQuestionEnum";
import { SurveyQuestionSubQuestion } from "./SurveyQuestionSubQuestion";
import { SurveyQuestionSubQuestionDouble } from "./SurveyQuestionSubQuestionDouble";

type SurveyQuestionContainerProps = {
  selected?: boolean;
  viewResource: SurveyViewResource;
  setSelected: (value: FormQuestion | null) => void;
  question: FormQuestion | undefined;
  updateQuestion: (value: FormQuestion) => void;
  addQuestion: (displayOrder: number) => void;
  copyQuestion: (displayOrder: number, question: FormQuestion) => void;
  navigateEditMode: () => void;
  branchableQuestions: FormQuestion[];
  branchableSections: FormSection[];
  refreshSurvey: Form | undefined;
  validationErrors: Object | undefined;
};

export const SurveyQuestionContainer = ({
  selected,
  viewResource,
  setSelected,
  question,
  updateQuestion,
  addQuestion,
  copyQuestion,
  navigateEditMode,
  branchableQuestions,
  branchableSections,
  refreshSurvey,
  validationErrors,
}: SurveyQuestionContainerProps) => {
  const [formQuestion, setFormQuestion] = useState<FormQuestion | undefined>(question);
  const [answers, setAnswers] = useState<FormAnswer[]>(question?.formAnswers || []);
  const [subQuestions, setSubQuestions] = useState<FormSubQuestion[]>(question?.formSubQuestions || []);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const properties = viewResource.formQuestionProperties || [];

  useEffect(() => {
    if (refreshSurvey) {
      const matchedQuestion = refreshSurvey?.formQuestions?.find((q) => q.id == question?.id);
      setAnswers(matchedQuestion?.formAnswers || []);
      setSubQuestions(matchedQuestion?.formSubQuestions || []);
    }
  }, [refreshSurvey]);

  useEffect(() => {
    updateQuestion(formQuestion!);
  }, [formQuestion]);

  useEffect(() => {
    setFormQuestion(
      produce((draft) => {
        draft!.formAnswers = answers!;
      })
    );
  }, [answers]);

  useEffect(() => {
    setFormQuestion(
      produce((draft) => {
        draft!.formSubQuestions = subQuestions!;
      })
    );
  }, [subQuestions]);

  const addNewQuestion = () => {
    var displayOrder = formQuestion?.displayOrder! + 1;
    addQuestion(displayOrder);
  };

  const copyNewQuestion = () => {
    var displayOrder = formQuestion?.displayOrder! + 1;
    copyQuestion(displayOrder, formQuestion!);
  };

  const addNewAnswer = () => {
    const displayOrder: number = answers?.length! + 1;

    setAnswers(
      produce((draft) => {
        let newAnswer: FormAnswer = createEntity<FormAnswer>({
          displayOrder,
          name: `Answer ${displayOrder}`,
        });

        draft?.push(newAnswer);
      })
    );
  };

  const addNewSubQuestion = () => {
    let displayOrder: number =
      subQuestions?.filter((sq) => !sq.branchedFromSubQuestionId && !sq.branchToSubQuestionTempId).length! + 1;
    const newGuid = crypto.randomUUID();

    let arr: FormSubQuestion[] = [];
    let newSubQuestion: FormSubQuestion = createEntity<FormSubQuestion>({
      displayOrder,
      name: `Sub-Question ${displayOrder}`,
      label: `${displayOrder}`,
      tempId: newGuid,
    });

    arr.push(newSubQuestion);

    if (formQuestion?.formQuestionTypeId == FormQuestionTypes.DoubleMatrix) {
      let alternateSubQuestion: FormSubQuestion = createEntity<FormSubQuestion>({
        displayOrder: displayOrder,
        name: `Sub-Question ${displayOrder} (Branched)`,
        label: `${displayOrder}`,
        tempId: crypto.randomUUID(),
        branchToSubQuestionTempId: newGuid,
      });

      arr.push(alternateSubQuestion);
    }

    setSubQuestions(
      produce((draft) => {
        draft.push(...arr);
      })
    );
  };

  const onDragEnd = (result) => {
    const { source, destination, type } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }

    const sID = source.droppableId;
    const dID = destination.droppableId;

    if (type == "subQuestions") {
      if (sID === dID) {
        const tempSubQuestions = reorder<FormSubQuestion>(subQuestions, source.index, destination.index);
        setSubQuestions(tempSubQuestions);
      }
    } else {
      if (sID === dID) {
        const tempFormAnswers = reorder<FormAnswer>(answers, source.index, destination.index);
        setAnswers(tempFormAnswers);
      }
    }
  };

  const updateAnswer = (answer: FormAnswer) => {
    let index = answers.findIndex((a) => a.id == answer.id && a.displayOrder == answer.displayOrder);
    if (index != -1) {
      setAnswers(
        produce((draft) => {
          draft![index!] = answer;
        })
      );
    }
  };

  const updateSubQuestion = (subQuestion: FormSubQuestion) => {
    let index = subQuestions.findIndex((q) => q.id == subQuestion.id && q.displayOrder == subQuestion.displayOrder);
    if (index != -1) {
      setSubQuestions(
        produce((draft) => {
          draft![index!] = subQuestion;
        })
      );
    }
  };

  const deleteQuestion = (e) => {
    if (window.confirm(DELETE_WARNING)) {
      e.stopPropagation();
      setFormQuestion(
        produce((draft) => {
          draft!.deleted = true;
        })
      );
    }
  };

  const onFormQuestionTypeChange = (formQuestion: FormQuestion, value: LkFormQuestionType) => {
    setFormQuestion(
      produce((draft) => {
        if (value.id == FormQuestionTypes.MatrixGrouped) {
          draft!.instruction = "";
          draft!.label = "MG";
        }
        draft!.formQuestionTypeId = value?.id;
      })
    );
  };

  const getValidationError = () => {
    if (validationErrors?.[`formQuestionTypeId`]) return validationErrors?.[`formQuestionTypeId`];
    else if (validationErrors?.[`formQuestionWriteInTypeId`]) return validationErrors?.[`formQuestionWriteInTypeId`];
    else if (validationErrors?.[`label`]) return validationErrors?.[`label`];
    else if (validationErrors?.[`name`]) return validationErrors?.[`name`];
    return null;
  };

  const formQuestionTypeField = () => {
    const propertyName = pn<FormQuestion>("formQuestionTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);
    return (
      <Grid item md={3} xs={12}>
        <Select
          label={property?.displayName}
          modelProperty={property}
          value={viewResource?.formQuestionTypes?.find((x) => x.id === formQuestion?.formQuestionTypeId)}
          options={viewResource?.formQuestionTypes}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => onFormQuestionTypeChange(formQuestion!, value)}
        />
      </Grid>
    );
  };

  const formQuestionWriteInTypeField = () => {
    const propertyName = pn<FormQuestion>("formQuestionWriteInTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);
    return (
      <Grid item md={4} xs={12}>
        <Select
          required
          label={property?.displayName}
          modelProperty={property}
          value={viewResource?.formQuestionWriteInTypes?.find((x) => x.id === formQuestion?.formQuestionWriteInTypeId)}
          options={viewResource?.formQuestionWriteInTypes}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            setFormQuestion(
              produce((draft) => {
                draft!.formQuestionWriteInTypeId = value?.id;
              })
            )
          }
        />
      </Grid>
    );
  };

  const propertyField = () => {
    return (
      <Grid item md={3} xs={12}>
        {!selected && (
          <DataDisplay label="Property">
            {
              viewResource?.applicationProperties?.find((x) => x.id === question?.formPropertyId)
                ?.name
            }
          </DataDisplay>
        )}
        {selected && (
          <Select
            label="Property"
            //errors={validationErrors?.[pn<Application>("parentApplicationId")]}
            value={viewResource?.applicationProperties?.find(
              (x) => x.id === question?.formPropertyId
            )}
            options={viewResource?.applicationProperties}
            getOptionLabel={(option) => option.name}
            onChange={(e, value) => {
              setFormQuestion(
                produce((draft) => {
                  draft!.formPropertyId = value?.id;
                })
              )

              // Clear entityId for each answer
              setAnswers(prevAnswers =>
                prevAnswers.map(answer => ({
                  ...answer,
                  entityId: undefined,
                }))
              );
            }
            }
          />
        )}
      </Grid>
    );
  };

  const answerWidthField = () => {
    return (
      <Grid item md={3} xs={12}>
        <Input
          label="Answer Width (%)"
          type="number"
          value={formQuestion?.writeInWidthPercent}
          onChange={(e) =>
            setFormQuestion(
              produce((draft) => {
                draft!.writeInWidthPercent = parseFloat(e.target.value);
              })
            )
          }
        />
      </Grid>
    );
  };

  const defaultAnswerField = () => {
    return (
      <Grid item md={3} xs={12}>
        <Input
          label="Default Answer"
          value={formQuestion?.defaultAnswer}
          onChange={(e) =>
            setFormQuestion(
              produce((draft) => {
                draft!.defaultAnswer = e.target.value;
              })
            )
          }
        />
      </Grid>
    );
  };

  const labelField = () => {
    return (
      <Input
        disabled={formQuestion?.formQuestionTypeId == FormQuestionTypes.MatrixGrouped}
        label="Label"
        value={formQuestion?.label}
        onChange={(e) =>
          setFormQuestion(
            produce((draft) => {
              draft!.label = e.target.value;
            })
          )
        }
      />
    );
  };

  const questionWidthField = () => {
    return (
      <Grid item md={2} xs={12}>
        <Input
          label="Question Width (%)"
          type="number"
          value={formQuestion?.questionWidthPercent}
          onChange={(e) =>
            setFormQuestion(
              produce((draft) => {
                draft!.questionWidthPercent = parseFloat(e.target.value);
              })
            )
          }
        />
      </Grid>
    );
  };

  const questionField = () => {
    return (
      <RichTextEditor
        hasColor
        hasAlign
        placeholder="Question"
        value={formQuestion?.name}
        onChange={(value) =>
          setFormQuestion(
            produce((draft) => {
              draft!.name = value!;
            })
          )
        }
      />
    );
  };

  const branchToFormQuestionField = () => {
    const propertyName = pn<FormQuestion>("branchToFormQuestionTempId");
    const property = properties.find((x) => x.propertyName === propertyName);
    return (
      <Select
        label={property?.displayName}
        modelProperty={property}
        value={branchableQuestions?.find((x) => x.tempId == formQuestion?.branchToFormQuestionTempId)}
        options={branchableQuestions}
        getOptionLabel={(option) => option.label || "No Label"}
        onChange={(e, value) =>
          setFormQuestion(
            produce((draft) => {
              if (value == null) {
                draft!.branchToFormQuestionId = undefined;
                draft!.branchToFormQuestionTempId = undefined;
              } else {
                draft!.branchToFormQuestionTempId = value?.tempId;
              }
            })
          )
        }
      />
    );
  };

  const branchToFormSectionField = () => {
    const propertyName = pn<FormQuestion>("branchToFormSectionTempId");
    const property = properties.find((x) => x.propertyName === propertyName);
    return (
      <Grid item md={4} xs={12}>
        {selected && (
          <Select
            label={property?.displayName}
            modelProperty={property}
            value={branchableSections?.find((x) => x.tempId == formQuestion?.branchToFormSectionTempId)}
            options={branchableSections}
            getOptionLabel={(option) => option.name.replace(/<[^>]*>/g, "") || "No Label"}
            onChange={(e, value) =>
              setFormQuestion(
                produce((draft) => {
                  if (value == null) {
                    draft!.branchToFormSectionId = undefined;
                    draft!.branchToFormSectionTempId = undefined;
                  } else {
                    draft!.branchToFormSectionTempId = value?.tempId;
                  }
                })
              )
            }
          />
        )}
      </Grid>
    );
  };

  const instructionField = () => {
    return (
      <Grid item md={8} xs={12}>
        {!selected && formQuestion?.instruction && (
          <RichTextReader label="Instructions" html={formQuestion?.instruction} />
        )}
        {selected && (
          <RichTextEditor
            hasColor
            hasAlign
            label="Instructions"
            placeholder="Place Instructions here"
            value={formQuestion?.instruction}
            onChange={(value) =>
              setFormQuestion(
                produce((draft) => {
                  draft!.instruction = value!;
                })
              )
            }
          />
        )}
      </Grid>
    );
  };

  const hideSubQuestionLabelField = () => {
    return (
      <Grid item xs={3}>
        <Switch
          label="Hide Subquestion Label"
          onClick={() => {
            setFormQuestion(
              produce((draft) => {
                draft!.hideSubQuestionLabel = !formQuestion?.hideSubQuestionLabel;
              })
            );
          }}
          checked={formQuestion?.hideSubQuestionLabel}
        ></Switch>
      </Grid>
    );
  };

  const alternateRowField = () => {
    return (
      <Grid item xs={3}>
        <Switch
          label="Alternate Row Color"
          onClick={() => {
            setFormQuestion(
              produce((draft) => {
                draft!.isAlternatingRowColor = !formQuestion?.isAlternatingRowColor;
              })
            );
          }}
          checked={formQuestion?.isAlternatingRowColor}
        ></Switch>
      </Grid>
    );
  };

  const isVerticalTextField = () => {
    return (
      <Grid item xs={3}>
        <Switch
          label="Vertical Text"
          onClick={() => {
            setFormQuestion(
              produce((draft) => {
                draft!.verticalAnswerText = !formQuestion?.verticalAnswerText;
              })
            );
          }}
          checked={formQuestion?.verticalAnswerText}
        ></Switch>
      </Grid>
    );
  };

  const alternativeText1Field = () => {
    return (
      <Grid item md={8} xs={12}>
        {!selected && formQuestion?.alternativeText1 && (
          <RichTextReader label="Alt. Text 1" html={formQuestion?.alternativeText1} />
        )}
        {selected && (
          <RichTextEditor
            hasColor
            hasAlign
            label="Alt. Text 1"
            placeholder="Left Hand Side Here..."
            value={formQuestion?.alternativeText1}
            onChange={(value) =>
              setFormQuestion(
                produce((draft) => {
                  draft!.alternativeText1 = value!;
                })
              )
            }
          />
        )}
      </Grid>
    );
  };

  const alternativeText2Field = () => {
    return (
      <Grid item md={8} xs={12}>
        {!selected && formQuestion?.alternativeText2 && (
          <RichTextReader label="Alt. Text 2" html={formQuestion?.alternativeText2} />
        )}
        {selected && (
          <RichTextEditor
            hasColor
            hasAlign
            label="Alt. Text 2"
            placeholder="Right Hand Side Here..."
            value={formQuestion?.alternativeText2}
            onChange={(value) =>
              setFormQuestion(
                produce((draft) => {
                  draft!.alternativeText2 = value!;
                })
              )
            }
          />
        )}
      </Grid>
    );
  };

  const doubleMatrixField = () => {
    return (
      <Grid item md={10} xs={12}>
        <Grid container spacing="sm">
          <Grid item xs={9}>
            <DataDisplay label="Sub-Questions">
              <Stack direction="column">
                {subQuestions
                  ?.filter((a) => !a.deleted && !a.branchedFromSubQuestionId && !a.branchToSubQuestionTempId)
                  .map((a, index) => {
                    return (
                      <SurveyQuestionSubQuestionDouble
                        branchedFromSubQuestionId={a.branchedFromSubQuestionId}
                        updateSubQuestion={updateSubQuestion}
                        key={`${a.id}:${a.displayOrder}:left`}
                        index={index}
                        selected={selected}
                        subQuestion={a}
                      />
                    );
                  })}

                {selected && (
                  <Box px={20}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        addNewSubQuestion();
                      }}
                    >
                      <Tooltip title="Add New Sub Question">
                        <AddCircleOutlineIcon />
                      </Tooltip>
                    </IconButton>
                  </Box>
                )}
              </Stack>
            </DataDisplay>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Grid
        item
        xs={10}
        onClick={() => {
          setSelected(formQuestion!);
          navigateEditMode();
        }}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Card onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <Grid container spacing="sm">
              <Grid item xs={12}>
                <Typography color="error">{getValidationError()}</Typography>
              </Grid>
              <Grid item xs={11}>
                <Grid container spacing="sm">
                  <Grid item md={1} xs={12}>
                    {!selected && (
                      <Stack direction="row">
                        <Typography>{formQuestion?.label}</Typography>
                        {formQuestion?.isMandatory && <Typography color="error">*</Typography>}
                      </Stack>
                    )}
                    {selected && <>{labelField()}</>}
                  </Grid>

                  {selected && (
                    <>
                      {formQuestionTypeField()}

                      {formQuestion?.formQuestionTypeId == FormQuestionTypes.OpenEnded &&
                        formQuestionWriteInTypeField()}

                      {propertyField()} 

                      {(formQuestion?.formQuestionTypeId == FormQuestionTypes.OpenEnded ||
                        formQuestion?.formQuestionTypeId == FormQuestionTypes.MultipleChoiceDropDown) && answerWidthField()}

                      {formQuestion?.formQuestionTypeId == FormQuestionTypes.OpenEnded && defaultAnswerField()}

                      {enumContains(formQuestion?.formQuestionTypeId, FormQuestionTypesWithSubQuestions) &&
                        questionWidthField()}

                      <GridBreak />
                    </>
                  )}

                  <Grid item md={8} xs={12}>
                    {!selected && <RichTextReader html={formQuestion?.name || "Untitled Question"} />}
                    {selected && <>{questionField()}</>}
                  </Grid>

                  <Grid item md={4} xs={12}>
                    {selected && <>{branchToFormQuestionField()}</>}
                  </Grid>

                  <GridBreak />

                  {formQuestion?.formQuestionTypeId != FormQuestionTypes.DoubleMatrix && instructionField()}

                  {branchToFormSectionField()}

                  <GridBreak />

                  {selected &&
                    formQuestion?.formQuestionTypeId == FormQuestionTypes.DoubleMatrix &&
                    alternativeText1Field()}

                  {selected &&
                    formQuestion?.formQuestionTypeId == FormQuestionTypes.DoubleMatrix &&
                    alternativeText2Field()}

                  <GridBreak />

                  {/* Form SubQuestions */}

                  {selected &&
                    enumContains(formQuestion?.formQuestionTypeId, FormQuestionTypesWithSubQuestions) &&
                    hideSubQuestionLabelField()}

                  {selected &&
                    enumContains(formQuestion?.formQuestionTypeId, FormQuestionTypesWithSubQuestions) &&
                    alternateRowField()}

                  {selected &&
                    formQuestion?.formQuestionTypeId == FormQuestionTypes.DoubleMatrix &&
                    isVerticalTextField()}

                  <GridBreak />

                  {enumContains(formQuestion?.formQuestionTypeId, FormQuestionTypesWithSubQuestions) &&
                    formQuestion?.formQuestionTypeId != FormQuestionTypes.DoubleMatrix && (
                      <Grid item md={8} xs={12}>
                        {subQuestions && formQuestion && (
                          <DataDisplay label="Sub-Questions">
                            <List>
                              <MyDroppable
                                id={`subQuestion:${formQuestion!.id}:${formQuestion!.displayOrder}`}
                                type="subQuestions"
                              >
                                {subQuestions
                                  ?.filter((a) => !a.deleted)
                                  .map((a, index) => {
                                    return (
                                      <SurveyQuestionSubQuestion
                                        updateSubQuestion={updateSubQuestion}
                                        key={`${a.id}:${a.displayOrder}`}
                                        index={index}
                                        selected={selected}
                                        subQuestion={a}
                                      />
                                    );
                                  })}

                                {selected && (
                                  <Box px={20}>
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addNewSubQuestion();
                                      }}
                                    >
                                      <Tooltip title="Add New Sub Question">
                                        <AddCircleOutlineIcon />
                                      </Tooltip>
                                    </IconButton>
                                  </Box>
                                )}
                              </MyDroppable>
                            </List>
                          </DataDisplay>
                        )}
                      </Grid>
                    )}

                  {formQuestion?.formQuestionTypeId == FormQuestionTypes.DoubleMatrix && doubleMatrixField()}

                  <GridBreak />

                  {/* Form Answers */}
                  <Grid item md={8} xs={12}>
                    {enumContains(formQuestion?.formQuestionTypeId, FormQuestionTypesWithAnswers) && (
                      <DataDisplay label="Answers">
                        <List>
                          <MyDroppable
                            id={`formAnswers:${formQuestion!.id}:${formQuestion!.displayOrder}`}
                            type="formAnswers"
                          >
                            {answers
                              ?.filter((a) => !a.deleted)
                              .map((a, index) => {
                                return (
                                  <SurveyQuestionAnswer
                                    key={`${a.id}:${a.displayOrder}`}
                                    index={index}
                                    selected={selected}
                                    updateAnswer={updateAnswer}
                                    answer={a}
                                    branchableQuestions={branchableQuestions}
                                    isBranchable={enumContains(
                                      formQuestion?.formQuestionTypeId,
                                      BranchableFormQuestionTypes
                                    )}
                                    viewResource={viewResource}
                                  />
                                );
                              })}
                          </MyDroppable>

                          {selected && (
                            <Box px={20}>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addNewAnswer();
                                }}
                              >
                                <Tooltip title="Add New Answer">
                                  <AddCircleOutlineIcon />
                                </Tooltip>
                              </IconButton>
                            </Box>
                          )}
                        </List>
                      </DataDisplay>
                    )}
                  </Grid>

                  <GridBreak />

                  <Grid item md={3} xs={4}>
                    {selected && (
                      <Switch
                        label="Required"
                        onClick={() => {
                          setFormQuestion(
                            produce((draft) => {
                              draft!.isMandatory = !formQuestion?.isMandatory;
                            })
                          );
                        }}
                        checked={formQuestion?.isMandatory}
                      ></Switch>
                    )}
                  </Grid>

                  <Grid item md={3} xs={4}>
                    {selected && (
                      <Switch
                        label="New Page"
                        onClick={() => {
                          setFormQuestion(
                            produce((draft) => {
                              draft!.startNewPage = !formQuestion?.startNewPage;
                            })
                          );
                        }}
                        checked={formQuestion?.startNewPage}
                      ></Switch>
                    )}
                  </Grid>

                  <Grid item md={3} xs={4}>
                    {selected && (
                      <Switch
                        label="Hide Label"
                        onClick={() => {
                          setFormQuestion(
                            produce((draft) => {
                              draft!.hideLabel = !formQuestion?.hideLabel;
                            })
                          );
                        }}
                        checked={formQuestion?.hideLabel}
                      ></Switch>
                    )}
                  </Grid>

                  <Grid item md={3} xs={4}>
                    {selected && (
                      <Switch
                        label="Hide Label and Question"
                        onClick={() => {
                          setFormQuestion(
                            produce((draft) => {
                              draft!.hideQuestionName = !formQuestion?.hideQuestionName;
                            })
                          );
                        }}
                        checked={formQuestion?.hideQuestionName}
                      ></Switch>
                    )}
                  </Grid>

                  <Grid item md={3} xs={4}>
                    {selected && (
                      <Tooltip title="Marks this as the last question. No other questions will appear after this one.">
                        <Switch
                          label="Is End Question"
                          onClick={() => {
                            setFormQuestion(
                              produce((draft) => {
                                draft!.isEndQuestion = !formQuestion?.isEndQuestion;
                              })
                            );
                          }}
                          checked={formQuestion?.isEndQuestion}
                        ></Switch>
                      </Tooltip>
                    )}
                  </Grid>

                  <Grid item md={3} xs={4}>
                    {selected && (
                      <Tooltip title="Indicates whether this question and its responses should be hidden from the Client Survey Export feature">
                        <Switch
                          label="Hide Export"
                          onClick={() => {
                            setFormQuestion(
                              produce((draft) => {
                                draft!.hideFromExport = !formQuestion?.hideFromExport;
                              })
                            );
                          }}
                          checked={formQuestion?.hideFromExport}
                        ></Switch>
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={1} display="flex" direction="column" alignItems="center" justifyContent="space-between">
                <Box>
                  {selected && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(null);
                      }}
                    >
                      <ExpandLessIcon />
                    </IconButton>
                  )}
                </Box>
                <Box />
              </Grid>
            </Grid>
          </Card>
        </DragDropContext>
      </Grid>

      {selected && (
        <Grid item xs={2} display="flex" alignItems="center" justifyContent="center">
          <Card sx={{ maxWidth: "100px" }}>
            <Stack direction="column" spacing="md">
              <IconButton onClick={() => addNewQuestion()}>
                <Tooltip title="Add New Question">
                  <AddCircleOutlineIcon />
                </Tooltip>
              </IconButton>

              <IconButton onClick={() => copyNewQuestion()}>
                <Tooltip title="Copy Question">
                  <ContentCopyIcon />
                </Tooltip>
              </IconButton>

              <DeleteIconButton
                onClick={(e) => {
                  deleteQuestion(e);
                }}
              />
            </Stack>
          </Card>
        </Grid>
      )}
    </>
  );
};
